import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Drawer } from './ui/Drawer';
import {
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import ModalHeader from './ui/ModalHeader';
import { useModalDrag } from '../hooks/useModalDrag';

// Page route mappings for linkification
const PAGE_ROUTES: Record<string, string> = {
  'live fleet map': '/fleet-management/live-map',
  'live map': '/fleet-management/live-map',
  'fleet map': '/fleet-management/live-map',
  'chassis map': '/fleet-management/chassis-map',
  'chassis tracker': '/fleet-management/chassis-map',
  'dispatch planner': '/dispatching/dispatch-planner',
  'dispatch dashboard': '/dispatching/dispatch-dashboard',
  'smart dispatch': '/dispatching/smart-dispatch',
  'smart preplanning': '/dispatching/smart-preplanning',
  'at risk monitor': '/dispatching/at-risk-monitor',
  'driver scores': '/safety/driver-scores',
  'safety reports': '/safety/report-center?report=driver-report',
  'driver report': '/safety/report-center?report=driver-report',
  'hours of service': '/safety/hours-of-service',
  'equipment list': '/data-management/equipment-list',
  'field manager': '/data-management/field-manager',
  'locations': '/data-management/locations',
  'aircall': '/analytics/aircall',
  'home': '/home',
  'drivers': '/fleet-management/drivers',
};

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: string[];
  timestamp: Date;
  isHtml?: boolean; // If true, render content as HTML
}

interface GWI360AssistantProps {
  isOpen: boolean;
  onClose: () => void;
}

// Spotlight Search Overlay
const SpotlightOverlay: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (question: string) => void;
  isLoading: boolean;
  onViewHistory?: () => void;
  onOpenChat?: () => void;
  hasHistory?: boolean;
}> = ({ isOpen, onClose, onSubmit, isLoading, onViewHistory, onOpenChat, hasHistory = false }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim() && !isLoading) {
      onSubmit(query.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] bg-scrim/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="w-full max-w-2xl mx-4 animate-in fade-in duration-200"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="bg-panel rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <ModalHeader
              title={
                <span className="flex items-center gap-3">
                  <SparklesIcon className="w-5 h-5 text-blue-300" />
                  GWI360 Assistant
                </span>
              }
              onClose={onClose}
              dragHandleProps={dragHandleProps}
            />

            {/* Input */}
            <div className="flex items-center gap-3 px-5 py-4">
              <MagnifyingGlassIcon className="w-6 h-6 text-gray-400 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask a question about GWI360..."
                className="flex-1 text-lg bg-transparent border-none outline-none text-heading placeholder-gray-400 dark:placeholder-gray-500"
                disabled={isLoading}
              />
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              ) : query.trim() && (
                <button
                  type="submit"
                  className="p-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Hint and Actions */}
            <div className="px-5 pb-3 flex items-center justify-between gap-4">
              <div className="text-xs text-muted">
                Press <kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs">Enter</kbd> to search or <kbd className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs">Esc</kbd> to close
              </div>
              <div className="flex items-center gap-3">
                {onOpenChat && (
                  <button
                    type="button"
                    onClick={onOpenChat}
                    className="text-xs text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 font-medium transition-colors"
                  >
                    Open Chat
                  </button>
                )}
                {hasHistory && onViewHistory && (
                  <button
                    type="button"
                    onClick={onViewHistory}
                    className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                  >
                    View History
                  </button>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Parse content and convert page references to clickable links + field names to highlight buttons + entity links
const linkifyContent = (
  content: string,
  onNavigate: (path: string) => void,
  onAskAbout?: (question: string) => void,
  onHighlightField?: (fieldId: string) => void,
  onFetchProDetails?: (proNumber: string) => void
): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let currentText = content;
  let keyIndex = 0;

  // Step 1: Extract field names in backticks (like `field_name`)
  const fieldPattern = /`([a-z_][a-z0-9_]*)`/gi;
  const segments: Array<{ type: 'text' | 'field'; content: string }> = [];
  let lastFieldIndex = 0;
  let fieldMatch;

  while ((fieldMatch = fieldPattern.exec(content)) !== null) {
    // Add text before field
    if (fieldMatch.index > lastFieldIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastFieldIndex, fieldMatch.index),
      });
    }
    // Add field name
    segments.push({
      type: 'field',
      content: fieldMatch[1], // The field name without backticks
    });
    lastFieldIndex = fieldPattern.lastIndex;
  }
  // Add remaining text
  if (lastFieldIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastFieldIndex),
    });
  }

  // Step 2: Process segments - linkify page names in text segments
  segments.forEach((segment, segmentIndex) => {
    if (segment.type === 'field') {
      // Render field name as highlight button
      parts.push(
        <button
          key={`field-${keyIndex++}`}
          onClick={() => {
            if (onHighlightField) {
              onHighlightField(segment.content);
              // Auto-clear is handled by the callback itself
            }
          }}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-xs hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors"
          title="Click to highlight this field"
        >
          <span>{segment.content}</span>
        </button>
      );
    } else {
      // Linkify entities (PRO numbers, driver IDs) and page names in text segments
      let processedContent = segment.content;
      const entityMatches: Array<{
        type: 'pro' | 'driver' | 'page';
        start: number;
        end: number;
        text: string;
        value: string;
      }> = [];

      // Find PRO numbers in various formats
      // Look for any 7-digit number in the content
      const allSevenDigitPattern = /\b(\d{7})\b/g;
      let sevenDigitMatch;

      while ((sevenDigitMatch = allSevenDigitPattern.exec(processedContent)) !== null) {
        const proNumber = sevenDigitMatch[1];
        const matchStart = sevenDigitMatch.index;
        const matchEnd = matchStart + proNumber.length;

        // Check context around the number (50 chars before and after)
        const contextBefore = processedContent.slice(Math.max(0, matchStart - 50), matchStart);
        const contextAfter = processedContent.slice(matchEnd, Math.min(processedContent.length, matchEnd + 20));
        const fullContext = contextBefore + processedContent.slice(matchStart, matchEnd) + contextAfter;

        // Treat as PRO if:
        // 1. Has "PRO" keyword nearby
        // 2. In a list with other numbers (commas, &, "and")
        // 3. After a colon suggesting it's a reference number
        // 4. Contains words like "load", "order", "assignment", "move"
        const isPROContext =
          /PRO/i.test(fullContext) ||
          /load|order|assignment|move|import|export/i.test(contextBefore) ||
          /[,:&]\s*$/.test(contextBefore) ||
          /^\s*[,&]/.test(contextAfter);

        if (isPROContext) {
          // Check for "PRO" prefix in the match itself
          let displayText = proNumber;
          let displayStart = matchStart;

          // Check if there's a "PRO" prefix right before the number
          const prefixMatch = /PRO\s*#?\s*$/i.exec(contextBefore);
          if (prefixMatch && contextBefore.length - prefixMatch[0].length === contextBefore.trimEnd().length) {
            displayStart = matchStart - prefixMatch[0].length;
            displayText = processedContent.slice(displayStart, matchEnd);
          }

          entityMatches.push({
            type: 'pro',
            start: displayStart,
            end: matchEnd,
            text: displayText,
            value: proNumber
          });
        }
      }

      // Find driver IDs (e.g., "driver 008", "driver 1234", "Driver 008")
      const driverPattern = /driver\s+(\d{3,4})\b/gi;
      let driverMatch;
      while ((driverMatch = driverPattern.exec(processedContent)) !== null) {
        entityMatches.push({
          type: 'driver',
          start: driverMatch.index,
          end: driverMatch.index + driverMatch[0].length,
          text: driverMatch[0],
          value: driverMatch[1]
        });
      }

      // Find page names
      const pageNames = Object.keys(PAGE_ROUTES);
      const pagePattern = new RegExp(`\\b(${pageNames.join('|')})\\b`, 'gi');
      let pageMatch;
      while ((pageMatch = pagePattern.exec(processedContent)) !== null) {
        const route = PAGE_ROUTES[pageMatch[0].toLowerCase()];
        if (route) {
          entityMatches.push({
            type: 'page',
            start: pageMatch.index,
            end: pageMatch.index + pageMatch[0].length,
            text: pageMatch[0],
            value: route
          });
        }
      }

      // Sort matches by start position
      entityMatches.sort((a, b) => a.start - b.start);

      // Remove overlapping matches (keep first match)
      const nonOverlappingMatches = [];
      let lastEnd = 0;
      for (const match of entityMatches) {
        if (match.start >= lastEnd) {
          nonOverlappingMatches.push(match);
          lastEnd = match.end;
        }
      }

      // Build parts from matches
      let textIndex = 0;
      for (const match of nonOverlappingMatches) {
        // Add text before match
        if (match.start > textIndex) {
          parts.push(
            <span key={`text-${segmentIndex}-${keyIndex++}`}>
              {processedContent.slice(textIndex, match.start)}
            </span>
          );
        }

        // Add clickable entity
        if (match.type === 'pro') {
          parts.push(
            <button
              key={`pro-${keyIndex++}`}
              onClick={() => onFetchProDetails && onFetchProDetails(match.value)}
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 font-semibold text-xs hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
              title="Click to view PRO details"
            >
              {match.text}
            </button>
          );
        } else if (match.type === 'driver') {
          parts.push(
            <button
              key={`driver-${keyIndex++}`}
              onClick={() => onAskAbout && onAskAbout(`Tell me about driver ${match.value}`)}
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 font-semibold text-xs hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
              title="Click to get details about this driver"
            >
              {match.text}
            </button>
          );
        } else if (match.type === 'page') {
          parts.push(
            <button
              key={`page-${keyIndex++}`}
              onClick={() => onNavigate(match.value)}
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline font-medium"
            >
              {match.text}
            </button>
          );
        }

        textIndex = match.end;
      }

      // Add remaining text
      if (textIndex < processedContent.length) {
        parts.push(
          <span key={`text-${segmentIndex}-${keyIndex++}`}>
            {processedContent.slice(textIndex)}
          </span>
        );
      }
    }
  });

  return parts.length > 0 ? parts : [content];
};

// Chat Message Component
const ChatMessage: React.FC<{
  message: Message;
  onNavigate: (path: string) => void;
  onAskAbout?: (question: string) => void;
  onHighlightField?: (fieldId: string) => void;
  onFetchProDetails?: (proNumber: string) => void;
}> = ({ message, onNavigate, onAskAbout, onHighlightField, onFetchProDetails }) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        <div
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? 'bg-blue-600 text-white rounded-br-md'
              : 'bg-gray-100 dark:bg-gray-700 text-heading rounded-bl-md'
          }`}
        >
          {message.isHtml ? (
            <div
              className="text-sm prose prose-sm dark:prose-invert max-w-none [&_table]:w-full [&_table]:text-xs [&_th]:bg-gray-200 [&_th]:dark:bg-gray-600 [&_th]:px-2 [&_th]:py-1 [&_td]:px-2 [&_td]:py-1 [&_td]:border-b [&_td]:border-gray-200 [&_td]:dark:border-gray-600"
              dangerouslySetInnerHTML={{ __html: message.content }}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                // Handle clicks on driver links
                if (target.classList.contains('driver-link')) {
                  const driverId = target.getAttribute('data-driver-id');
                  if (driverId && onAskAbout) {
                    onAskAbout(`tell me about driver ${driverId}`);
                  }
                }
                // Handle clicks on PRO links
                if (target.classList.contains('pro-link')) {
                  const proNumber = target.getAttribute('data-pro-number');
                  if (proNumber && onFetchProDetails) {
                    onFetchProDetails(proNumber);
                  }
                }
              }}
            />
          ) : (
            <p className="text-sm whitespace-pre-wrap">
              {isUser ? message.content : linkifyContent(message.content, onNavigate, onAskAbout, onHighlightField, onFetchProDetails)}
            </p>
          )}
        </div>
        {message.sources && message.sources.length > 0 && (
          <div className="mt-1 text-xs text-muted">
            Sources: {message.sources.join(', ')}
          </div>
        )}
        <div className={`mt-1 text-xs text-gray-400 ${isUser ? 'text-right' : 'text-left'}`}>
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
};

export const GWI360Assistant: React.FC<GWI360AssistantProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setHighlightedFieldId } = useDisplayPreferences();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSpotlight, setShowSpotlight] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [viewMode, setViewMode] = useState<'chat' | 'history'>('chat');
  const [historySearch, setHistorySearch] = useState('');
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [historyLoadFailed, setHistoryLoadFailed] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Timer refs for cleanup
  const highlightClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollIntoViewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (highlightClearTimeoutRef.current) clearTimeout(highlightClearTimeoutRef.current);
      if (scrollIntoViewTimeoutRef.current) clearTimeout(scrollIntoViewTimeoutRef.current);
    };
  }, []);

  // Navigate to a page while keeping the drawer open
  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    // Drawer stays open - no state changes needed
  }, [navigate]);

  // Highlight a field in the UI (auto-clears after 5 seconds)
  const handleHighlightField = useCallback((fieldId: string) => {
    // Clear any existing timeout
    if (highlightClearTimeoutRef.current) {
      clearTimeout(highlightClearTimeoutRef.current);
      highlightClearTimeoutRef.current = null;
    }

    setHighlightedFieldId(fieldId || null);

    // Scroll field into view if possible
    if (fieldId) {
      scrollIntoViewTimeoutRef.current = setTimeout(() => {
        const element = document.querySelector(`[data-field-name="${fieldId}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Auto-clear highlight after 5 seconds
      highlightClearTimeoutRef.current = setTimeout(() => {
        setHighlightedFieldId(null);
      }, 5000);
    }
  }, [setHighlightedFieldId]);

  // Fetch PRO details and display as HTML formatted message in chat
  const handleFetchProDetails = useCallback(async (proNumber: string) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/orders/order-details/${proNumber}`);
      const data = response.data.data;

      // Build legs table rows
      const legsRows = (data.legs || []).map((leg: any) => `
        <tr>
          <td>${leg.legnum}</td>
          <td>${leg.origName || 'Unknown'}</td>
          <td>${leg.destName || 'Unknown'}</td>
          <td>${leg.driver || '-'}</td>
          <td><span style="color: ${leg.loadedLeg ? 'rgb(var(--c-green-500))' : 'rgb(var(--sem-text-muted))'}">${leg.loadedLeg ? 'Loaded' : 'Empty'}</span></td>
        </tr>
      `).join('');

      // Format PRO details as HTML
      const htmlContent = `
        <div style="font-size: 13px;">
          <div style="font-weight: 600; font-size: 15px; margin-bottom: 12px; color: rgb(var(--c-blue-500));">
            PRO# ${data.pronumber} <span style="font-weight: 400; color: rgb(var(--sem-text-muted));">- ${data.moveType || 'N/A'}</span>
          </div>

          <table style="width: 100%; margin-bottom: 12px;">
            <tr>
              <td style="width: 50%; vertical-align: top; padding-right: 8px;">
                <div style="font-weight: 600; margin-bottom: 4px;">Customer</div>
                <div>${data.customerName || 'N/A'}</div>
                <div style="color: rgb(var(--sem-text-muted)); font-size: 11px;">Bill To: ${data.billToName || 'N/A'}</div>
                ${data.keyAccount ? '<div style="color: rgb(var(--c-purple-600)); font-size: 11px; font-weight: 500;">⭐ KEY ACCOUNT</div>' : ''}
              </td>
              <td style="width: 50%; vertical-align: top;">
                <div style="font-weight: 600; margin-bottom: 4px;">Equipment</div>
                <div>${data.container || 'N/A'} <span style="background: rgb(var(--c-blue-100)); color: rgb(var(--c-blue-700)); padding: 1px 6px; border-radius: 4px; font-size: 11px;">${data.size || 'N/A'}</span></div>
                ${data.chassisId ? `<div style="color: rgb(var(--sem-text-muted)); font-size: 11px;">Chassis: ${data.chassisId}</div>` : ''}
              </td>
            </tr>
          </table>

          ${data.lastFreeDay || data.cutoffDatetime ? `
          <div style="margin-bottom: 12px; padding: 8px; background: rgb(var(--c-amber-100)); border-radius: 6px;">
            <div style="font-weight: 600; font-size: 11px; color: rgb(var(--c-amber-800)); margin-bottom: 2px;">Key Dates</div>
            ${data.lastFreeDay ? `<div style="font-size: 12px;"><strong>LFD:</strong> ${data.lastFreeDay}</div>` : ''}
            ${data.cutoffDatetime ? `<div style="font-size: 12px;"><strong>Cutoff:</strong> ${data.cutoffDatetime}</div>` : ''}
          </div>
          ` : ''}

          <div style="font-weight: 600; margin-bottom: 6px;">Legs</div>
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: rgb(var(--sem-header-bg));">
                <th style="text-align: left; padding: 4px 8px; font-weight: 600;">#</th>
                <th style="text-align: left; padding: 4px 8px; font-weight: 600;">Origin</th>
                <th style="text-align: left; padding: 4px 8px; font-weight: 600;">Destination</th>
                <th style="text-align: left; padding: 4px 8px; font-weight: 600;">Driver</th>
                <th style="text-align: left; padding: 4px 8px; font-weight: 600;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${legsRows || `<tr><td colspan="5" style="text-align: center; color: rgb(var(--sem-text-muted));">No legs found</td></tr>`}
            </tbody>
          </table>
        </div>
      `;

      const proDetailsMessage: Message = {
        id: `pro-${proNumber}-${Date.now()}`,
        role: 'assistant',
        content: htmlContent,
        timestamp: new Date(),
        isHtml: true,
      };

      setMessages(prev => [...prev, proDetailsMessage]);
    } catch (error) {
      console.error('Failed to fetch PRO details:', error);
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `Sorry, I couldn't fetch details for PRO ${proNumber}. Please try again.`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load chat history from API
  const loadHistory = useCallback(async (offset: number = 0, prepend: boolean = false) => {
    if (loadingHistory) return;

    setLoadingHistory(true);
    try {
      const response = await api.get('/help-chat/history', {
        params: { limit: 20, offset }
      });

      const data = response.data;
      const historyMessages: Message[] = [];

      // Convert history to messages (reverse order - oldest first)
      data.history.reverse().forEach((item: any) => {
        // Check if answer contains HTML content
        const answerHasHtml = item.answer && (
          item.answer.includes('<div') ||
          item.answer.includes('<table') ||
          item.answer.includes('<span')
        );
        historyMessages.push(
          {
            id: `history-${item.id}-q`,
            role: 'user',
            content: item.question,
            timestamp: new Date(item.timestamp)
          },
          {
            id: `history-${item.id}-a`,
            role: 'assistant',
            content: item.answer,
            sources: item.sources,
            timestamp: new Date(item.timestamp),
            isHtml: answerHasHtml
          }
        );
      });

      if (prepend) {
        setMessages(prev => [...historyMessages, ...prev]);
      } else {
        setMessages(historyMessages);
      }

      setHasMoreHistory(data.has_more);
      setHistoryOffset(offset + data.count);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [loadingHistory]);

  // Note: We no longer auto-load history - each conversation starts fresh
  // Users can view history via the "View History" button

  // Detect scroll to top and load more
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop < 100 && hasMoreHistory && !loadingHistory) {
        const oldScrollHeight = container.scrollHeight;
        loadHistory(historyOffset, true).then(() => {
          // Maintain scroll position after prepending
          setTimeout(() => {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - oldScrollHeight;
          }, 50);
        });
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMoreHistory, loadingHistory, historyOffset, loadHistory]);

  // Scroll to bottom when messages change (but not when loading history)
  useEffect(() => {
    if (!loadingHistory) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loadingHistory]);

  // Focus input when drawer opens
  useEffect(() => {
    if (drawerOpen && inputRef.current) {
      const timeoutId = setTimeout(() => inputRef.current?.focus(), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [drawerOpen]);

  // Reset state when isOpen changes
  useEffect(() => {
    if (isOpen) {
      setShowSpotlight(true);
      setDrawerOpen(false);
    } else {
      setShowSpotlight(false);
      setDrawerOpen(false);
      // Keep messages for session, but could clear here if desired
    }
  }, [isOpen]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Build conversation history from existing messages (last 10 for context)
      const conversationHistory = messages.slice(-10).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await api.post('/help-chat/chat', {
        message: content.trim(),
        page_context: location.pathname,
        conversation_history: conversationHistory.length > 0 ? conversationHistory : undefined,
      });

      const data = response.data;

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.answer,
        sources: data.sources,
        timestamp: new Date(),
        isHtml: data.is_html || false,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Help chat error:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again or contact your administrator if the problem persists.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, location.pathname]);

  const handleSpotlightSubmit = useCallback(async (question: string) => {
    setShowSpotlight(false);
    setDrawerOpen(true);
    await sendMessage(question);
  }, [sendMessage]);

  // Ask about an entity (PRO, driver, etc.) - sends as new message
  const handleAskAbout = useCallback((question: string) => {
    sendMessage(question);
  }, [sendMessage]);

  // Open chat drawer without asking a question
  const handleOpenChat = useCallback(() => {
    setShowSpotlight(false);
    setDrawerOpen(true);
  }, []);

  const handleDrawerSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleClose = () => {
    setShowSpotlight(false);
    setDrawerOpen(false);
    onClose();
  };

  const handleNewConversation = () => {
    setMessages([]);
    setHistoryOffset(0);
    setHasMoreHistory(true);
    setDrawerOpen(false);
    setShowSpotlight(true);
  };

  // Load history items for the history view
  const loadHistoryItems = useCallback(async (search?: string) => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '50');
      if (search) params.append('search', search);

      const response = await api.get(`/help-chat/history?${params.toString()}`);
      setHistoryItems(response.data.history || []);
      setHistoryLoadFailed(false);
    } catch (error) {
      console.error('Failed to load history:', error);
      setHistoryItems([]);
      setHistoryLoadFailed(true);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleViewHistory = useCallback(() => {
    setShowSpotlight(false);
    setDrawerOpen(true);
    setViewMode('history');
    loadHistoryItems();
  }, [loadHistoryItems]);

  const handleBackToChat = useCallback(() => {
    setViewMode('chat');
  }, []);

  const handleHistorySearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    loadHistoryItems(historySearch);
  }, [historySearch, loadHistoryItems]);

  const handleSelectHistoryItem = useCallback((item: any) => {
    // Load this Q&A into the chat
    const timestamp = item.timestamp ? new Date(item.timestamp) : new Date();
    // Check if answer contains HTML content (structured HTML tags, not just any < character)
    const answerHasHtml = item.answer && (
      item.answer.includes('<div') ||
      item.answer.includes('<table') ||
      item.answer.includes('<span')
    );
    const userMsg: Message = {
      id: `hist-${item.id}-q`,
      role: 'user',
      content: item.question,
      timestamp,
    };
    const assistantMsg: Message = {
      id: `hist-${item.id}-a`,
      role: 'assistant',
      content: item.answer || 'No answer recorded',
      timestamp,
      isHtml: answerHasHtml,
    };
    setMessages([userMsg, assistantMsg]);
    setViewMode('chat');
  }, []);

  if (!isOpen && !drawerOpen) return null;

  return (
    <>
      {/* Spotlight Overlay */}
      <SpotlightOverlay
        isOpen={isOpen && showSpotlight}
        onClose={handleClose}
        onSubmit={handleSpotlightSubmit}
        isLoading={isLoading}
        onViewHistory={handleViewHistory}
        onOpenChat={handleOpenChat}
        hasHistory={messages.length > 0}
      />

      {/* Chat Drawer */}
      <Drawer
        isOpen={drawerOpen}
        onClose={handleClose}
        title={
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-blue-500" />
            <span>GWI360 Assistant</span>
          </div>
        }
        position="right"
        width="600px"
      >
        <div className="flex flex-col h-full">
          {/* Tab Buttons */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 px-4">
            <button
              onClick={() => setViewMode('chat')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'chat'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-muted hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Chat
            </button>
            <button
              onClick={() => {
                setViewMode('history');
                if (historyItems.length === 0) loadHistoryItems();
              }}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                viewMode === 'history'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-muted hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              History
            </button>
            {viewMode === 'chat' && messages.length > 0 && (
              <button
                onClick={handleNewConversation}
                className="ml-auto px-3 py-2 text-xs text-muted hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                New Chat
              </button>
            )}
          </div>

          {/* History View */}
          {viewMode === 'history' && (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Search Bar */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <form onSubmit={handleHistorySearch} className="flex gap-2">
                  <input
                    type="text"
                    value={historySearch}
                    onChange={(e) => setHistorySearch(e.target.value)}
                    placeholder="Search your conversations..."
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-panel text-heading placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  />
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors"
                  >
                    Search
                  </button>
                </form>
              </div>

              {/* History List */}
              <div className="flex-1 overflow-y-auto">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : historyItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted">
                    <MagnifyingGlassIcon className="w-10 h-10 mb-2 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm">
                      {historyLoadFailed
                        ? 'Couldn\'t load history — the request failed. Try again.'
                        : 'No conversations found'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100 dark:divide-gray-700">
                    {historyItems.map((item) => {
                      const itemDate = item.timestamp ? new Date(item.timestamp) : new Date();
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleSelectHistoryItem(item)}
                          className="w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          <div className="text-sm font-medium text-heading line-clamp-2 mb-1">
                            {item.question}
                          </div>
                          <div className="text-xs text-muted">
                            {itemDate.toLocaleDateString()} at {itemDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chat View */}
          {viewMode === 'chat' && (
            <>
              {/* Messages Area */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
                {messages.length === 0 && !loadingHistory ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-muted">
                    <SparklesIcon className="w-12 h-12 mb-3 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm">Ask me anything about GWI360!</p>
                  </div>
                ) : (
                  <>
                    {/* Loading older messages indicator */}
                    {loadingHistory && (
                      <div className="flex justify-center py-2">
                        <div className="text-xs text-muted flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          Loading older messages...
                        </div>
                      </div>
                    )}

                    {messages.map(message => (
                      <ChatMessage
                        key={message.id}
                        message={message}
                        onNavigate={handleNavigate}
                        onAskAbout={handleAskAbout}
                        onHighlightField={handleHighlightField}
                        onFetchProDetails={handleFetchProDetails}
                      />
                    ))}
                    {isLoading && (
                      <div className="flex justify-start mb-4">
                        <div className="bg-gray-100 dark:bg-gray-700 rounded-2xl rounded-bl-md px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

              {/* Input Area */}
              <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-800/50">
                <form onSubmit={handleDrawerSubmit} className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Ask a follow-up question..."
                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-panel text-heading placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    disabled={isLoading}
                  />
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isLoading}
                    className="p-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:dark:bg-gray-600 text-white transition-colors disabled:cursor-not-allowed"
                  >
                    <PaperAirplaneIcon className="w-5 h-5" />
                  </button>
                </form>
                <div className="mt-3 flex justify-end">
                  <span className="text-xs text-gray-400">
                    Powered by Anthropic (Claude Haiku)
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </Drawer>
    </>
  );
};

export default GWI360Assistant;
